import React, { useMemo } from 'react';
import clsx from 'clsx';
import I18n from '@/modules/i18n';
import Skeleton from '@/components/ui/Skeleton';
import { IAssignment, ISubmission } from '@/components/interfaces';
import { teacher_assignment_student_grading_path } from '@/modules/routes';
import assignmentLmsKind from '@/components/shared/Lms/assignmentLmsKind';
import { getSubmissionScore, toDisplayScore } from '@/components/staffer/AssignmentsBasedGradesTable/Table/utils';
import { IStudent } from '../../types';
import useGradesTableStore from '../../store/useGradesTableStore';
import StatusIndicator from './StatusIndicator';
import styles from '../styles.module.scss';
import SyncGradeStatusButton from './SyncGradeStatusButton';
import ViewGradeSyncActivityButton from './ViewGradeSyncActivityButton';

// aria-busy sits on the cell so a screen reader reports "loading" where the user is, not table-wide.
const StudentGradeCellSkeleton = () => (
  <div
    aria-busy
    className="tw-flex tw-items-center tw-justify-center tw-w-full tw-h-full"
    data-testid="student-grade-cell-skeleton"
  >
    <span className="sr-only">{I18n.t('loading')}</span>
    <div aria-hidden>
      <Skeleton bgColor="tw-bg-gray-300" className="tw-animate-skeleton-pulse" height="tw-h-4" width="tw-w-14" />
    </div>
  </div>
);

interface StudentGradeCellProps {
  assignment: IAssignment;
  lastCell?: boolean;
  student: IStudent;
  submission: ISubmission;
}

const StudentGradeCell = ({ assignment, lastCell = false, student, submission }: StudentGradeCellProps) => {
  const allAssignmentsAssigneeModels = useGradesTableStore(state => state.assignmentsAssigneeModels);
  const isLoadingSubmissions = useGradesTableStore(state => state.isLoadingSubmissions);
  const isSysadmin = useGradesTableStore(state => state.isSysadmin);
  const navigationParams = useGradesTableStore(state => state.navigationParams());
  const showLMSSyncStatus = useGradesTableStore(state => state.showLMSSyncStatus);
  const showPercentages = useGradesTableStore(state => state.showPercentages);

  const assignmentsAssigneeModels = useMemo(() => (
    allAssignmentsAssigneeModels.filter((aam) => {
      if (aam.assignment_id !== assignment.id) return false;
      if (aam.model_type === 'Student') return aam.model_id === student.id;

      return true; // classroom assignments are assigned to everyone
    })
  ), [assignment.id, student.id, allAssignmentsAssigneeModels]);

  const studentGrade = () => {
    const shouldDisplayNA = assignment.max_points.toString() === '0' && submission;

    return (
      <a
        className="tw-w-full tw-h-full tw-flex tw-justify-center tw-items-center"
        href={teacher_assignment_student_grading_path(assignment.id, student.id, navigationParams)}
      >
        {submission?.updated_since_graded && (
          <span className="tw-absolute tw-left-3 tw-top-1/2 -tw-translate-y-1/2 tw-text-blue tw-text-xs tw-font-bold">
            {I18n.t('updated')}
          </span>
        )}
        {shouldDisplayNA && !submission?.total_score && 'N/A'}
        {!shouldDisplayNA && toDisplayScore(
          getSubmissionScore(submission, '–'),
          assignment.max_points,
          showPercentages,
        )}
      </a>
    );
  };

  const isAssigned = assignmentsAssigneeModels.length > 0;

  if (isAssigned && isLoadingSubmissions) {
    return (
      <td className={clsx(styles.studentGradeCell, { [styles.lastCell]: lastCell })}>
        <StudentGradeCellSkeleton />
      </td>
    );
  }

  const assignmentConnectedToLMS = assignmentLmsKind(assignment) !== null;
  // AIDEV-NOTE: PLAT-18052 — score_updated_at, not just the submission's existence. A teacher opening
  // the grading view calls Submission.create_or_find_by (question_answers_controller.rb:207,
  // assessments_controller.rb:158), so a bare shell row appears with no score, no submitted_at and no
  // graded_at. Keying off `!!submission` therefore put a sync icon on students who had done nothing —
  // red before PLAT-18137, pending after. score_updated_at is set by Assignment#submit! and by
  // set_score_updated_at!, so it marks the point where a score exists and its sync status is
  // meaningful. It is also what deriveSyncStatus compares against, so gate and derivation agree.
  const showSyncStatus = showLMSSyncStatus
    && isAssigned
    && student.has_lms_account
    && assignmentConnectedToLMS
    && !!submission?.score_updated_at;

  return (
    <td
      key={`${student.id}-${assignment.id}`}
      className={clsx(styles.studentGradeCell, 'tw-transition-colors hover:tw-bg-tci-purple/15', {
        [styles.lastCell]: lastCell,
        'tw-bg-grey': !isAssigned,
      })}
    >
      <div className="tw-flex tw-items-center tw-justify-center tw-relative tw-h-full">
        <StatusIndicator
          assignment={assignment}
          isAssigned={isAssigned}
          student={student}
          submission={submission}
        />
        {isAssigned && (
          <>{studentGrade()}</>
        )}
        {/* Absolute so the score stays centred in the cell and the "Updated!" badge can overlay it.
            The ml-* values clear the score, which is why they differ by its width; pl-8 is the gap
            after it, sized for the widest raw score (a decimal like 100.5) rather than the
            percentage, which rounds. mt-0.5 sits on the group rather than on SyncGradeStatusButton
            so both icons shift together and stay aligned with each other. */}
        {showSyncStatus && (
          <div className={clsx(
            'tw-absolute tw-mt-0.5 tw-flex tw-items-center tw-gap-1.5 tw-pl-8',
            showPercentages ? 'tw-ml-16' : 'tw-ml-12',
          )}
          >
            <SyncGradeStatusButton
              assignmentId={assignment.id}
              scoreUpdatedAt={submission.score_updated_at}
              studentId={student.id}
              submissionId={submission.id}
            />
            {isSysadmin && (
              <ViewGradeSyncActivityButton
                assignmentId={assignment.id}
                student={student}
              />
            )}
          </div>
        )}
      </div>
    </td>
  );
};

export default StudentGradeCell;
